import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { getOlderMockExams, deleteUserMockData } from "../../../../../api/auth";
import { setTraceToZero, setMockName } from "../../../../../redux/reducers/question_reducer";
//import "./OlderExamsPage.css";

const OlderExamsPage = () => {
    const [olderMocks, setOlderMocks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const userDetails = useSelector(state => state.auth);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const user_id = useSelector((state) => state.auth.userId);

    // Dependencies for useEffect cleanup
    const uniName = userDetails?.universityName;
    const courseName = userDetails?.courseName;


    useEffect(() => {
        const fetchOlderExams = async () => {
            setLoading(true); // Ensure loading state is true on every fetch attempt
            
            setError(null);

            if (uniName && courseName && user_id) {
                try {
                    // Assuming the modified getOlderMockExams API call now takes user_id
                    const response = await getOlderMockExams(
                        uniName,
                        courseName,
                        user_id
                    );

                    // --- FIX: Safely check if 'response' exists before reading properties ---
                    if (response && response.success && response.mockExams) {
                        // Set the state with the enriched array including 'completed' status
                        setOlderMocks(response.mockExams);
                    } else {
                        // Display generic error if success is false or data format is wrong
                        // If response is null/undefined, this block is skipped, falling into catch
                        setError(response?.error || "Failed to load historical mock tests.");
                    }
                } catch (err) {
                    // This catch block handles network errors or errors where response is undefined
                    console.error("Failed to fetch older mocks:", err);
                    setError("Failed to load historical mock tests. Please try again later.");
                } finally {
                    setLoading(false);
                }
            } else {
                setLoading(false); // Stop loading if userDetails are not available
            }
        };

        // Note: Added user_id to dependencies since it is used in the fetch call
        fetchOlderExams();
    }, [uniName, courseName, user_id]);

    const handleStartMock = async (mockName) => {
        // Find the specific mock from state
        const selectedMock = olderMocks.find((mock) => mock.mock_name === mockName);

        // Only show confirmation if there's existing data to overwrite
        let proceed = true;

        if (selectedMock?.completed) {
            const confirmed = window.confirm(
                "Starting this mock test will overwrite your previous mock test scores. Do you want to continue?"
            );
            proceed = confirmed;
        }

        if (proceed) {
            try {
                // Only delete if previous data exists
                if (selectedMock?.completed) {
                    await deleteUserMockData(user_id, mockName);
                }

                dispatch(setTraceToZero());
                dispatch(setMockName(mockName));
                navigate(`/dashboard/question-bank/mocktestquestions`);
            } catch (error) {
                console.error("Failed to delete previous mock data:", error);
                alert("An error occurred while starting the mock. Please try again.");
            }
        } else {
            console.log("User cancelled starting the mock.");
        }
    };



    const handleViewResults = (mockName) => {
        dispatch(setMockName(mockName));

        navigate("/dashboard/mock-test/questions/results", {
            replace: true,
        });
        
        // Note: Removed dispatch and navigate for placeholder state.
        // --- END PLACEHOLDER ---
    };


    return (
        <div className="older-exams-container">
            <div className="paper">
                <h1 className="title">Historical Mock Exams</h1>
                <p className="subtitle">Select an exam below.</p>
                {loading && <div className="loading-message">Loading exams...</div>}
                {error && <div className="error-message">{error}</div>}

                {!loading && !error && (
                    <ul className="exam-list">
                        {olderMocks.length > 0 ? (
                            olderMocks.map((mock) => (
                                <li key={mock.mock_name} className="exam-item">
                                    <span className="exam-name">{mock.mock_name}</span>
                                    <div className="button-group">
                                        {/* Start Button (Always visible) */}
                                        <button
                                            onClick={() => handleStartMock(mock.mock_name)}
                                            className="exam-button start-button"
                                        // You might want to style the start button differently if already completed
                                        >
                                            Start Mock Exam
                                        </button>

                                        {/* View Results Button (Conditional and Placeholder) */}
                                        {mock.completed && (
                                            <button
                                                onClick={() => handleViewResults(mock.mock_name)}
                                                className="exam-button results-button"
                                            >
                                                View Results
                                            </button>
                                        )}
                                    </div>
                                </li>
                            ))
                        ) : (
                            <li className="no-exams-message">No historical mock exams found for your course.</li>
                        )}
                    </ul>
                )}
            </div>
        </div>
    );
};

export default OlderExamsPage;